import React, { useEffect } from 'react'
import 'leaflet/dist/leaflet.css'

import { Event, EventResult, Game, MemberResponse, Severity } from '../../../../shared/types'
import {
  Box,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import EditAttributesIcon from '@mui/icons-material/EditAttributes'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import TextInputComponent, { TextFieldType } from '../../../../shared/components/TextInputComponent'
import { useDispatch, useSelector } from 'react-redux'
import { getMembers } from '../../../../store/data/data.selectors'
import ButtonComponent from '../../../../shared/components/ButtonComponent'
import CheckBoxComponent from '../../../../shared/components/CheckBoxComponent'
import * as settingsActions from '../../../../store/settings/settings.actions'
import getPlayerResultsForEachRound from '../../../../util/getPlayerResultsForEachRound'
import { makeStyles } from '@mui/styles'
import getDefaultCompleteResult from '../../../../util/getDefaultCompleteResult'
import SaveIcon from '@mui/icons-material/Save'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import EditResultRound from './EditResultRound'
import { getApiVersion } from '../../../../shared/config/getApiVersion'
import Env from '../../../../shared/config/envConfig'

interface NewEventResultsProps {
  eventResult?: EventResult
  event?: Event
  saveAllowed: (value: boolean) => void
}

interface UpdatePlayerResult {
  memberId: string
  roundResult: number
  lostDefense: number
  lostOffense: number
  wonOffense: number
  defensePoints: number
  offensePoints: number
  completeResult: number
}

const useStyles = makeStyles((theme: Theme) => ({
  playerResult: {
    fontWeight: 'bold',
  },
  tableHeader: {
    textAlign: 'center',
    fontWeight: 'bold',
    verticalAlign: 'bottom',
  },
  dealer: {
    backgroundColor: 'lightgrey',
  },
  stickyFooter: {
    position: 'sticky',
    bottom: 0,
  },
  footer: {
    fontWeight: 'bold',
    textAlign: 'center',
  },
  footerTitle: {
    fontWeight: 'bold',
    textAlign: 'right',
  },
  baseBox: {
    position: 'relative',
  },
}))

const NewEventResults: React.FC<NewEventResultsProps> = (props) => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const restPath = Env.restBase
  const requestHeaders: HeadersInit = new Headers()
  requestHeaders.set('Accept', 'application/json')
  requestHeaders.set('Content-Type', 'application/json')

  const theme: Theme = useTheme()
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const downXl = useMediaQuery(theme.breakpoints.down('xl'))

  const members: MemberResponse | undefined = useSelector(getMembers)

  const [player1, setPlayer1] = React.useState<string | undefined>()
  const [player2, setPlayer2] = React.useState<string | undefined>()
  const [player3, setPlayer3] = React.useState<string | undefined>()
  const [player4, setPlayer4] = React.useState<string | undefined>()
  const [session, setSession] = React.useState<number | undefined>(undefined)
  const [table, setTable] = React.useState<number | undefined>(undefined)
  const [games, setgames] = React.useState<number[]>([])
  const [newEventResult, setNewEventResult] = React.useState<EventResult | undefined>(props.eventResult)
  const [editRound, setEditRound] = React.useState<number | undefined>(undefined)
  const [advancedEdit, setAdvancedEdit] = React.useState<boolean>(false)
  const [newRound, setNewRound] = React.useState<number | undefined>(undefined)
  const [editCompleteResult, setEditCompleteResult] = React.useState<boolean>(false)
  const [actualDealer, setActualDealer] = React.useState<string>()
  const [footerOpen, setFooterOpen] = React.useState<boolean>(false)

  useEffect(() => {
    let saveAllowed = true
    if (!!newRound || !!editRound || !!editCompleteResult) {
      saveAllowed = false
    }
    props.saveAllowed(saveAllowed)
  }, [newRound, editRound, editCompleteResult])

  useEffect(() => {
    console.log(newEventResult)
  }, [newEventResult])

  useEffect(() => {
    setNewRound(undefined)
    setEditRound(undefined)
    setNewEventResult(props.eventResult)
  }, [props.eventResult])

  const getPlayerName = (id: string) => {
    const member = members?.results.find(member => member.originId === id)
    return member ? `${member.firstname} ${member.lastname}` : null
  }

  const getPlayerInitial = (id: string) => {
    const member = members?.results.find(member => member.originId === id)
    return member ? `${member.firstname.charAt(0)}${member.lastname.charAt(0)}` : null
  }

  useEffect(() => {
    const resultList: EventResult | undefined = props.eventResult

    const games: number[] = []
    resultList && Object.keys(resultList?.games).map(round => {
      games.push(parseInt(round, 10))
    })
    setNewEventResult(resultList)
    setTable(resultList?.table)
    setSession(resultList?.session)
    setgames(games)
    setPlayer1(resultList?.players[0])
    setPlayer2(resultList?.players[1])
    setPlayer3(resultList?.players[2])
    if (resultList?.players.length === 4) {
      setPlayer4(resultList?.players[3])
    }
  }, [])

  return (
    <>
      <Typography><b>Durchgang: {session}</b></Typography>
      <Typography><b>Tisch: {table}</b></Typography>
      {!downMd ? (
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                className={classes.tableHeader}
                sx={{ width: '80px', maxWidth: '80px' }}
              >
                Runde
              </TableCell>
              <TableCell
                className={classes.tableHeader}
                sx={{ width: '100px', maxWidth: '100px' }}
              >
                Spielwert
              </TableCell>
              <TableCell className={classes.tableHeader} sx={{ minWidth: '100px' }}>
                {player1 && getPlayerName(player1)}
              </TableCell>
              <TableCell className={classes.tableHeader} sx={{ minWidth: '100px' }}>
                {player2 && getPlayerName(player2)}
              </TableCell>
              <TableCell className={classes.tableHeader} sx={{ minWidth: '100px' }}>
                {player3 && getPlayerName(player3)}
              </TableCell>
              {player4 &&
                <TableCell className={classes.tableHeader} sx={{ minWidth: '100px' }}>
                  {player4 && getPlayerName(player4)}
                </TableCell>}
              <TableCell className={classes.tableHeader} sx={{ width: '30px' }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {games?.map((round) => {
              return (
                <TableRow key={round}>
                  <TableCell align='center'>
                    {round}
                  </TableCell>
                  {newEventResult?.games[round].player ? (
                    <>
                      {editRound !== round
                        ? (
                          <>
                            <TableCell align='center'>
                              <Typography variant='body1'>
                                {newEventResult?.games[round].gameValue}
                              </Typography>
                            </TableCell>
                            <TableCell
                              className={(round % (player4 ? 4 : 3)) === 1 ? classes.dealer : ''}
                            >
                              <Grid container alignItems='center'>
                                <Grid item xs={6} sx={{ width: '30px', maxWidth: '30px', textAlign: 'center' }}>
                                  <Typography className={classes.playerResult}>
                                    {newEventResult?.games[round].game === Game.eingemischt ? ((round % (player4 ? 4 : 3)) === 1 ? (player1 && newEventResult?.games[round].results[player1] ? newEventResult?.games[round].results[player1] : 0) : '-') : (player1 && newEventResult?.games[round].player === player1 && newEventResult?.games[round].gameValue ? newEventResult?.games[round].results[player1] : '-')}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6} sx={{ display: 'flex', flexWrap: downXl ? 'wrap' : 'nowrap' }}>
                                  <CheckBoxComponent
                                    label='gew.'
                                    value={player1 && newEventResult?.games[round].player === player1 ? newEventResult?.games[round].won : false}
                                    disabled
                                    onChange={(value) => {
                                      if (newEventResult?.games[round] && value) {
                                        const tempResult = {
                                          ...newEventResult,
                                          games: {
                                            ...newEventResult?.games,
                                            [round]: {
                                              ...newEventResult?.games[round],
                                              win: true,
                                              player: player1,
                                            },
                                          },
                                        }
                                        tempResult && setNewEventResult(getPlayerResultsForEachRound(tempResult))
                                      }
                                    }}
                                  />
                                  <CheckBoxComponent
                                    label='verl.'
                                    value={player1 && newEventResult?.games[round].player === player1 ? !newEventResult?.games[round].won : false}
                                    disabled
                                    onChange={(value) => {
                                      if (newEventResult?.games[round] && value) {
                                        const tempResult = {
                                          ...newEventResult,
                                          games: {
                                            ...newEventResult?.games,
                                            [round]: {
                                              ...newEventResult?.games[round],
                                              win: false,
                                              player: player1,
                                            },
                                          },
                                        }
                                        tempResult && setNewEventResult(getPlayerResultsForEachRound(tempResult))
                                      }
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </TableCell>
                            <TableCell
                              className={(round % (player4 ? 4 : 3)) === 2 ? classes.dealer : ''}
                            >
                              <Grid container alignItems='center'>
                                <Grid item xs={6} sx={{ width: '30px', maxWidth: '30px', textAlign: 'center' }}>
                                  <Typography className={classes.playerResult}>
                                    {newEventResult?.games[round].game === Game.eingemischt ? ((round % (player4 ? 4 : 3)) === 2 ? (player2 && newEventResult?.games[round].results[player2] ? newEventResult?.games[round].results[player2] : 0) : '-') : (player2 && newEventResult?.games[round].player === player2 && newEventResult?.games[round].gameValue ? newEventResult?.games[round].results[player2] : '-')}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6} sx={{ display: 'flex', flexWrap: downXl ? 'wrap' : 'nowrap' }}>
                                  <CheckBoxComponent
                                    disabled
                                    onChange={(value) => {
                                      if (newEventResult?.games[round] && value) {
                                        const tempResult = {
                                          ...newEventResult,
                                          games: {
                                            ...newEventResult?.games,
                                            [round]: {
                                              ...newEventResult?.games[round],
                                              win: true,
                                              player: player2,
                                            },
                                          },
                                        }
                                        tempResult && setNewEventResult(getPlayerResultsForEachRound(tempResult))
                                      }
                                    }}
                                    label='gew.'
                                    value={player2 && newEventResult?.games[round].player === player2 ? newEventResult?.games[round].won : false}
                                  />
                                  <CheckBoxComponent
                                    disabled
                                    onChange={(value) => {
                                      if (newEventResult?.games[round] && value) {
                                        const tempResult = {
                                          ...newEventResult,
                                          games: {
                                            ...newEventResult?.games,
                                            [round]: {
                                              ...newEventResult?.games[round],
                                              win: false,
                                              player: player2,
                                            },
                                          },
                                        }
                                        tempResult && setNewEventResult(getPlayerResultsForEachRound(tempResult))
                                      }
                                    }}
                                    label='verl.'
                                    value={player2 && newEventResult?.games[round].player === player2 ? !newEventResult?.games[round].won : false}
                                  />
                                </Grid>
                              </Grid>
                            </TableCell>
                            <TableCell
                              className={(round % (player4 ? 4 : 3)) === (player4 ? 3 : 0) ? classes.dealer : ''}
                            >
                              <Grid container alignItems='center'>
                                <Grid item xs={6} sx={{ width: '30px', maxWidth: '30px', textAlign: 'center' }}>
                                  <Typography className={classes.playerResult}>
                                    {newEventResult?.games[round].game === Game.eingemischt ? ((round % (player4 ? 4 : 3)) === 0 ? (player3 && newEventResult?.games[round].results[player3] ? newEventResult?.games[round].results[player3] : 0) : '-') : (player3 && newEventResult?.games[round].player === player3 && newEventResult?.games[round].gameValue ? newEventResult?.games[round].results[player3] : '-')}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6} sx={{ display: 'flex', flexWrap: downXl ? 'wrap' : 'nowrap' }}>
                                  <CheckBoxComponent
                                    label='gew.'
                                    value={player3 && newEventResult?.games[round].player === player3 ? newEventResult?.games[round].won : false}
                                    disabled
                                    onChange={(value) => {
                                      if (newEventResult?.games[round] && value) {
                                        const tempResult = {
                                          ...newEventResult,
                                          games: {
                                            ...newEventResult?.games,
                                            [round]: {
                                              ...newEventResult?.games[round],
                                              win: true,
                                              player: player3,
                                            },
                                          },
                                        }
                                        tempResult && setNewEventResult(getPlayerResultsForEachRound(tempResult))
                                      }
                                    }}
                                  />
                                  <CheckBoxComponent
                                    label='verl.'
                                    value={player3 && newEventResult?.games[round].player === player3 ? !newEventResult?.games[round].won : false}
                                    disabled
                                    onChange={(value) => {
                                      if (newEventResult?.games[round] && value) {
                                        const tempResult = {
                                          ...newEventResult,
                                          games: {
                                            ...newEventResult?.games,
                                            [round]: {
                                              ...newEventResult?.games[round],
                                              win: false,
                                              player: player3,
                                            },
                                          },
                                        }
                                        tempResult && setNewEventResult(getPlayerResultsForEachRound(tempResult))
                                      }
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            </TableCell>
                            {player4 &&
                              <TableCell
                                className={(round % (player4 ? 4 : 3)) === 0 ? classes.dealer : ''}
                              >
                                <Grid container alignItems='center'>
                                  <Grid item xs={6} sx={{ width: '30px', maxWidth: '30px', textAlign: 'center' }}>
                                    <Typography className={classes.playerResult}>
                                      {newEventResult?.games[round]?.game === Game.eingemischt ? ((round % (player4 ? 4 : 3)) === 0 ? (player4 && newEventResult?.games[round].results[player4] ? newEventResult?.games[round].results[player4] : 0) : '-') : (player4 && newEventResult?.games[round].player === player4 && newEventResult?.games[round].gameValue ? newEventResult?.games[round].results[player4] : '-')}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6} sx={{ display: 'flex', flexWrap: downXl ? 'wrap' : 'nowrap' }}>
                                    <CheckBoxComponent
                                      label='gew.'
                                      value={player4 && newEventResult?.games[round]?.player === player4 ? newEventResult?.games[round].won : false}
                                      disabled
                                      onChange={(value) => {
                                        if (newEventResult?.games[round] && value) {
                                          const tempResult = {
                                            ...newEventResult,
                                            games: {
                                              ...newEventResult?.games,
                                              [round]: {
                                                ...newEventResult?.games[round],
                                                win: true,
                                                player: player4,
                                              },
                                            },
                                          }
                                          tempResult && setNewEventResult(getPlayerResultsForEachRound(tempResult))
                                        }
                                      }}
                                    />
                                    <CheckBoxComponent
                                      label='verl.'
                                      value={player4 && newEventResult?.games[round].player === player4 ? !newEventResult?.games[round].won : false}
                                      disabled
                                      onChange={(value) => {
                                        if (newEventResult?.games[round] && value) {
                                          const tempResult = {
                                            ...newEventResult,
                                            games: {
                                              ...newEventResult?.games,
                                              [round]: {
                                                ...newEventResult?.games[round],
                                                win: false,
                                                player: player4,
                                              },
                                            },
                                          }
                                          tempResult && setNewEventResult(getPlayerResultsForEachRound(tempResult))
                                        }
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              </TableCell>}
                            <TableCell>
                              <IconButton
                                disabled={!!newRound}
                                onClick={() => {
                                  setEditRound(round)
                                  setAdvancedEdit(false)
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                disabled={!!newRound}
                                onClick={() => {
                                  setEditRound(round)
                                  setAdvancedEdit(true)
                                }}
                              >
                                <EditAttributesIcon />
                              </IconButton>
                            </TableCell>
                          </>
                        )
                        : (
                          <>
                            {newEventResult?.players &&
                              <EditResultRound
                                id={props.eventResult?.id}
                                roundNumber={round}
                                round={newEventResult?.games[round]}
                                players={newEventResult?.players}
                                dealer={actualDealer}
                                eventId={props.event.id}
                                gameNumber={round}
                                eventResult={newEventResult}
                                advancedEdit={advancedEdit}
                              />}
                          </>
                        )}
                    </>)
                    : (
                      <>
                        {newEventResult?.players &&
                          <EditResultRound
                            id={props.eventResult?.id}
                            roundNumber={round}
                            round={newEventResult?.games[round]}
                            players={newEventResult?.players}
                            dealer={actualDealer}
                            eventId={props.event.id}
                            gameNumber={round}
                            eventResult={newEventResult}
                            advancedEdit={false}
                          />}
                      </>
                    )}
                </TableRow>)
            },
            )}
          </TableBody>
          {!footerOpen &&
            <TableHead className={classes.stickyFooter}>
              <TableRow>
                <TableCell
                  className={classes.footerTitle}
                  colSpan={2}
                >
                  <IconButton onClick={() => setFooterOpen(!footerOpen)}>
                    {footerOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                  Ergebnis
                </TableCell>
                <TableCell
                  className={classes.footer}
                >{player1 && newEventResult?.results && newEventResult?.results[player1]?.sessionResult}
                </TableCell>
                <TableCell
                  className={classes.footer}
                >{player2 && newEventResult?.results && newEventResult?.results[player2]?.sessionResult}
                </TableCell>
                <TableCell
                  className={classes.footer}
                >{player3 && newEventResult?.results && newEventResult?.results[player3]?.sessionResult}
                </TableCell>
                {player4 &&
                  <TableCell
                    className={classes.footer}
                  >{player4 && newEventResult?.results && newEventResult?.results[player4]?.sessionResult}
                  </TableCell>}
                <TableCell className={classes.footer} sx={{ width: '30px' }} />
              </TableRow>
            </TableHead>}
          {footerOpen &&
            <TableHead className={classes.stickyFooter} sx={{ width: '100%' }}>
              <TableRow>
                <TableCell
                  className={classes.footerTitle}
                  colSpan={2}
                >
                  <IconButton onClick={() => setFooterOpen(!footerOpen)}>
                    {footerOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                  Ergebnis
                </TableCell>
                <TableCell
                  className={classes.footer}
                >{player1 && newEventResult?.results && newEventResult?.results[player1]?.sessionResult}
                </TableCell>
                <TableCell
                  className={classes.footer}
                >{player2 && newEventResult?.results && newEventResult?.results[player2]?.sessionResult}
                </TableCell>
                <TableCell
                  className={classes.footer}
                >{player3 && newEventResult?.results && newEventResult?.results[player3]?.sessionResult}
                </TableCell>
                {player4 &&
                  <TableCell
                    className={classes.footer}
                  >{player4 && newEventResult?.results && newEventResult?.results[player4]?.sessionResult}
                  </TableCell>}
                <TableCell className={classes.footer} sx={{ width: '30px' }} />
              </TableRow>
              <TableRow sx={{ width: '100%}' }}>
                <TableCell
                  className={classes.footerTitle}
                  colSpan={2}
                >Gew. / Verl.
                </TableCell>
                <TableCell
                  className={classes.footer}
                >{`${player1 && newEventResult?.results && newEventResult?.results[player1]?.wonOffense} / ${player1 && newEventResult?.results && newEventResult?.results[player1]?.lostOffense}`}
                </TableCell>
                <TableCell
                  className={classes.footer}
                >{`${player2 && newEventResult?.results && newEventResult?.results[player2]?.wonOffense} / ${player2 && newEventResult?.results && newEventResult?.results[player2]?.lostOffense}`}
                </TableCell>
                <TableCell
                  className={classes.footer}
                >{`${player3 && newEventResult?.results && newEventResult?.results[player3]?.wonOffense} / ${player3 && newEventResult?.results && newEventResult?.results[player3]?.lostOffense}`}
                </TableCell>
                {player4 &&
                  <TableCell
                    className={classes.footer}
                  >{`${player4 && newEventResult?.results && newEventResult?.results[player4]?.wonOffense} / ${player4 && newEventResult?.results && newEventResult?.results[player4]?.lostOffense}`}
                  </TableCell>}
                <TableCell className={classes.footer} sx={{ width: '30px' }} />
              </TableRow>
              <TableRow>
                <TableCell className={classes.footerTitle} colSpan={2}>
                  +50 je gew. Spiel <br />
                  -50 je verl. Spiel
                </TableCell>
                <TableCell
                  className={classes.footer}
                >{player1 && newEventResult?.results && ((newEventResult?.results[player1]?.wonOffense || 0) - (newEventResult?.results[player1]?.lostOffense || 0)) * 50}
                </TableCell>
                <TableCell
                  className={classes.footer}
                >{player2 && newEventResult?.results && ((newEventResult?.results[player2]?.wonOffense || 0) - (newEventResult?.results[player2]?.lostOffense || 0)) * 50}
                </TableCell>
                <TableCell
                  className={classes.footer}
                >{player3 && newEventResult?.results && ((newEventResult?.results[player3]?.wonOffense || 0) - (newEventResult?.results[player3]?.lostOffense || 0)) * 50}
                </TableCell>
                {player4 &&
                  <TableCell
                    className={classes.footer}
                  >{player4 && newEventResult?.results && ((newEventResult?.results[player4]?.wonOffense || 0) - (newEventResult?.results[player4]?.lostOffense || 0)) * 50}
                  </TableCell>}
                <TableCell />
              </TableRow>
              <TableRow>
                <TableCell className={classes.footerTitle} colSpan={2}>
                  +{player4 ? 30 : 40} je verlorenes Spiel des Gegners
                </TableCell>
                <TableCell
                  className={classes.footer}
                >{player1 && newEventResult?.results && (newEventResult?.results[player1]?.lostDefense || 0) * (player4 ? 30 : 40)}
                </TableCell>
                <TableCell
                  className={classes.footer}
                >{player2 && newEventResult?.results && (newEventResult?.results[player2]?.lostDefense || 0) * (player4 ? 30 : 40)}
                </TableCell>
                <TableCell
                  className={classes.footer}
                >{player3 && newEventResult?.results && (newEventResult?.results[player3]?.lostDefense || 0) * (player4 ? 30 : 40)}
                </TableCell>
                {player4 &&
                  <TableCell
                    className={classes.footer}
                  >{player4 && newEventResult?.results && (newEventResult?.results[player4]?.lostDefense || 0) * 30}
                  </TableCell>}
                <TableCell />
              </TableRow>
              <TableRow>
                <TableCell className={classes.footerTitle} colSpan={2}>Gesamtergebnis
                </TableCell>
                <TableCell
                  className={classes.footer}
                >
                  {editCompleteResult ? (
                    <TextInputComponent
                      fullWidth
                      required
                      type={TextFieldType.number}
                      onChange={(value) => {
                        if (newEventResult?.results && player1) {
                          const tempResult = {
                            ...newEventResult,
                            results: {
                              ...newEventResult?.results,
                              [player1]: {
                                ...newEventResult?.results[player1],
                                completeResult: parseInt(value as string, 10),
                              },
                            },
                          }
                          setNewEventResult(tempResult)
                        }
                      }}
                      value={player1 && newEventResult?.results ? newEventResult?.results[player1]?.completeResult : 0}
                    />
                  ) : (
                    <>{player1 && newEventResult?.results && newEventResult?.results[player1]?.completeResult}</>
                  )}
                </TableCell>
                <TableCell
                  className={classes.footer}
                >
                  {editCompleteResult ? (
                    <TextInputComponent
                      fullWidth
                      required
                      type={TextFieldType.number}
                      onChange={(value) => {
                        if (newEventResult?.results && player2) {
                          const tempResult = {
                            ...newEventResult,
                            results: {
                              ...newEventResult?.results,
                              [player2]: {
                                ...newEventResult?.results[player2],
                                completeResult: parseInt(value as string, 10),
                              },
                            },
                          }
                          setNewEventResult(tempResult)
                        }
                      }}
                      value={player2 && newEventResult?.results ? newEventResult?.results[player2]?.completeResult : 0}
                    />
                  ) : (
                    <>{player2 && newEventResult?.results && newEventResult?.results[player2]?.completeResult}</>
                  )}
                </TableCell>
                <TableCell
                  className={classes.footer}
                >
                  {editCompleteResult ? (
                    <TextInputComponent
                      fullWidth
                      required
                      type={TextFieldType.number}
                      onChange={(value) => {
                        if (newEventResult?.results && player3) {
                          const tempResult = {
                            ...newEventResult,
                            results: {
                              ...newEventResult?.results,
                              [player3]: {
                                ...newEventResult?.results[player3],
                                completeResult: parseInt(value as string, 10),
                              },
                            },
                          }
                          setNewEventResult(tempResult)
                        }
                      }}
                      value={player3 && newEventResult?.results ? newEventResult?.results[player3]?.completeResult : 0}
                    />
                  ) : (
                    <>{player3 && newEventResult?.results && newEventResult?.results[player3]?.completeResult}</>
                  )}
                </TableCell>
                {player4 &&
                  <TableCell
                    className={classes.footer}
                  >
                    {editCompleteResult ? (
                      <TextInputComponent
                        fullWidth
                        required
                        type={TextFieldType.number}
                        onChange={(value) => {
                          if (newEventResult?.results && player4) {
                            const tempResult = {
                              ...newEventResult,
                              results: {
                                ...newEventResult?.results,
                                [player4]: {
                                  ...newEventResult?.results[player4],
                                  completeResult: parseInt(value as string, 10),
                                },
                              },
                            }
                            setNewEventResult(tempResult)
                          }
                        }}
                        value={player4 && newEventResult?.results ? newEventResult?.results[player4]?.completeResult : 0}
                      />
                    ) : (
                      <>{player4 && newEventResult?.results && newEventResult?.results[player4]?.completeResult}</>
                    )}
                  </TableCell>}
                <TableCell>
                  {editCompleteResult ? (
                    <IconButton onClick={() => {
                      setEditCompleteResult(false)
                      // Speichern des completeResults
                      const restUrl = `${restPath}/api/${getApiVersion()}/results`
                      const playerResults: UpdatePlayerResult[] = []
                      newEventResult && Object.keys(newEventResult.results).forEach(member => {
                        playerResults.push({
                          memberId: member,
                          roundResult: newEventResult?.results[member].sessionResult,
                          lostDefense: newEventResult?.results[member].lostDefense,
                          lostOffense: newEventResult?.results[member].lostOffense,
                          wonOffense: newEventResult?.results[member].wonOffense,
                          defensePoints: newEventResult?.results[member].defensePoints,
                          offensePoints: newEventResult?.results[member].offensePoints,
                          completeResult: newEventResult?.results[member].completeResult,
                        })
                      })

                      const body = {
                        table: newEventResult?.table,
                        session: newEventResult?.session,
                        eventId: props.event?.id,
                        playerResults: playerResults,
                      }
                      fetch(restUrl,
                        {
                          method: 'PUT',
                          body: JSON.stringify(body),
                          credentials: 'include',
                          headers: requestHeaders,
                        })
                        .then(res => res.json())
                        .then((json) => {
                          setNewEventResult(json.results.find(elem => elem.table === newEventResult?.table && elem.session === newEventResult?.session))
                        })
                        .catch(err => console.log(err))
                    }}
                    ><SaveIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      onClick={() => setEditCompleteResult(true)}
                    ><EditIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>}
        </Table>
      ) : (
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                padding='none'
                className={classes.tableHeader}
                sx={{ width: '30px', maxWidth: '30px' }}
              >
                Nr.
              </TableCell>
              <TableCell
                padding='none'
                className={classes.tableHeader}
                sx={{ width: '30px', maxWidth: '30px' }}
              >
                Spiel
              </TableCell>
              <TableCell padding='none' className={classes.tableHeader} sx={{ minWidth: '70px' }}>
                {player1 && getPlayerInitial(player1)}
              </TableCell>
              <TableCell padding='none' className={classes.tableHeader} sx={{ minWidth: '70px' }}>
                {player2 && getPlayerInitial(player2)}
              </TableCell>
              <TableCell padding='none' className={classes.tableHeader} sx={{ minWidth: '70px' }}>
                {player3 && getPlayerInitial(player3)}
              </TableCell>
              {player4 &&
                <TableCell padding='none' className={classes.tableHeader} sx={{ minWidth: '70px' }}>
                  {player4 && getPlayerInitial(player4)}
                </TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {games.map((round) => {
              return (
                <TableRow key={round}>
                  <TableCell align='left' padding='none'>
                    {editRound && editRound === round ? (
                      <IconButton
                        onClick={() => console.log('SAVING IS BUGGY > Mobile???')/* TODO saveChanges(round) */}
                      ><SaveIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        disabled={!!newRound}
                        onClick={() => setEditRound(round)}
                      ><EditIcon />
                      </IconButton>
                    )}
                    <Typography variant='caption'>{round}</Typography>
                  </TableCell>
                  {newEventResult?.games[round].player ? (
                    <>
                      <TableCell align='center' padding='none'>
                        {editRound !== round
                          ? (
                            <Typography variant='caption'>
                              {newEventResult?.games[round].gameValue || 0}
                            </Typography>
                          ) : (
                            <TextInputComponent
                              fullWidth
                              required
                              type={TextFieldType.number}
                              label='Spielwert'
                              onChange={(value) => {
                                if (newEventResult?.games[round].gameValue) {
                                  const tempResult = {
                                    ...newEventResult,
                                    games: {
                                      ...newEventResult?.games,
                                      [round]: {
                                        ...newEventResult?.games[round],
                                        gameValue: parseInt(games as string, 10),
                                      },
                                    },
                                  }
                                  tempResult && setNewEventResult(getPlayerResultsForEachRound(tempResult))
                                }
                              }}
                              value={newEventResult?.games[round].gameValue || 0}
                            />)}
                      </TableCell>
                      <TableCell
                        padding='none'
                        className={(round % (player4 ? 4 : 3)) === 1 ? classes.dealer : ''}
                      >
                        <Grid container alignItems='center'>
                          <Grid item xs={6} sx={{ width: '20px', maxWidth: '20px', textAlign: 'center' }}>
                            <Typography variant='caption' className={classes.playerResult}>
                              {player1 && newEventResult?.games[round].player === player1 && newEventResult?.games[round].gameValue ? newEventResult?.games[round].results[player1] : '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ display: 'flex', flexWrap: 'wrap' }}>
                            <CheckBoxComponent
                              label={<CheckIcon />}
                              value={player1 && newEventResult?.games[round].player === player1 ? newEventResult?.games[round].won : false}
                              disabled={editRound !== round}
                              onChange={(value) => {
                                if (newEventResult?.games[round] && value) {
                                  const tempResult = {
                                    ...newEventResult,
                                    games: {
                                      ...newEventResult?.games,
                                      [round]: {
                                        ...newEventResult?.games[round],
                                        win: true,
                                        player: player1,
                                      },
                                    },
                                  }
                                  tempResult && setNewEventResult(getPlayerResultsForEachRound(tempResult))
                                }
                              }}
                            />
                            <CheckBoxComponent
                              label={<CloseIcon />}
                              value={player1 && newEventResult?.games[round].player === player1 ? !newEventResult?.games[round].won : false}
                              disabled={editRound !== round}
                              onChange={(value) => {
                                if (newEventResult?.games[round] && value) {
                                  const tempResult = {
                                    ...newEventResult,
                                    games: {
                                      ...newEventResult?.games,
                                      [round]: {
                                        ...newEventResult?.games[round],
                                        win: false,
                                        player: player1,
                                      },
                                    },
                                  }
                                  tempResult && setNewEventResult(getPlayerResultsForEachRound(tempResult))
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                      </TableCell>
                      <TableCell
                        padding='none'
                        className={(round % (player4 ? 4 : 3)) === 2 ? classes.dealer : ''}
                      >
                        <Grid container alignItems='center'>
                          <Grid item xs={6} sx={{ width: '20px', maxWidth: '20px', textAlign: 'center' }}>
                            <Typography variant='caption' className={classes.playerResult}>
                              {player2 && newEventResult?.games[round].player === player2 && newEventResult?.games[round].gameValue ? newEventResult?.games[round].results[player2] : '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ display: 'flex', flexWrap: 'wrap' }}>
                            <CheckBoxComponent
                              label={<CheckIcon />}
                              disabled={editRound !== round}
                              onChange={(value) => {
                                if (newEventResult?.games[round] && value) {
                                  const tempResult = {
                                    ...newEventResult,
                                    games: {
                                      ...newEventResult?.games,
                                      [round]: {
                                        ...newEventResult?.games[round],
                                        win: true,
                                        player: player2,
                                      },
                                    },
                                  }
                                  tempResult && setNewEventResult(getPlayerResultsForEachRound(tempResult))
                                }
                              }}
                              value={player2 && newEventResult?.games[round].player === player2 ? newEventResult?.games[round].won : false}
                            />
                            <CheckBoxComponent
                              label={<CloseIcon />}
                              disabled={editRound !== round}
                              onChange={(value) => {
                                if (newEventResult?.games[round] && value) {
                                  const tempResult = {
                                    ...newEventResult,
                                    games: {
                                      ...newEventResult?.games,
                                      [round]: {
                                        ...newEventResult?.games[round],
                                        win: false,
                                        player: player2,
                                      },
                                    },
                                  }
                                  tempResult && setNewEventResult(getPlayerResultsForEachRound(tempResult))
                                }
                              }}
                              value={player2 && newEventResult?.games[round].player === player2 ? !newEventResult?.games[round].won : false}
                            />
                          </Grid>
                        </Grid>
                      </TableCell>
                      <TableCell
                        padding='none'
                        className={(round % (player4 ? 4 : 3)) === (player4 ? 3 : 0) ? classes.dealer : ''}
                      >
                        <Grid container alignItems='center'>
                          <Grid item xs={6} sx={{ width: '20px', maxWidth: '20px', textAlign: 'center' }}>
                            <Typography variant='caption' className={classes.playerResult}>
                              {player3 && newEventResult?.games[round].player === player3 && newEventResult?.games[round].gameValue ? newEventResult?.games[round].results[player3] : '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ display: 'flex', flexWrap: 'wrap' }}>
                            <CheckBoxComponent
                              label={<CheckIcon />}
                              value={player3 && newEventResult?.games[round].player === player3 ? newEventResult?.games[round].won : false}
                              disabled={editRound !== round}
                              onChange={(value) => {
                                if (newEventResult?.games[round] && value) {
                                  const tempResult = {
                                    ...newEventResult,
                                    games: {
                                      ...newEventResult?.games,
                                      [round]: {
                                        ...newEventResult?.games[round],
                                        win: true,
                                        player: player3,
                                      },
                                    },
                                  }
                                  tempResult && setNewEventResult(getPlayerResultsForEachRound(tempResult))
                                }
                              }}
                            />
                            <CheckBoxComponent
                              label={<CloseIcon />}
                              value={player3 && newEventResult?.games[round].player === player3 ? !newEventResult?.games[round].won : false}
                              disabled={editRound !== round}
                              onChange={(value) => {
                                if (newEventResult?.games[round] && value) {
                                  const tempResult = {
                                    ...newEventResult,
                                    games: {
                                      ...newEventResult?.games,
                                      [round]: {
                                        ...newEventResult?.games[round],
                                        win: false,
                                        player: player3,
                                      },
                                    },
                                  }
                                  tempResult && setNewEventResult(getPlayerResultsForEachRound(tempResult))
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                      </TableCell>
                      {player4 &&
                        <TableCell
                          padding='none'
                          className={(round % (player4 ? 4 : 3)) === 0 ? classes.dealer : ''}
                        >
                          <Grid container alignItems='center'>
                            <Grid item xs={6} sx={{ width: '20px', maxWidth: '20px', textAlign: 'center' }}>
                              <Typography variant='caption' className={classes.playerResult}>
                                {player4 && newEventResult?.games[round].player === player4 && newEventResult?.games[round].gameValue ? newEventResult?.games[round].results[player4] : '-'}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ display: 'flex', flexWrap: 'wrap' }}>
                              <CheckBoxComponent
                                label={<CheckIcon />}
                                value={player4 && newEventResult?.games[round].player === player4 ? newEventResult?.games[round].won : false}
                                disabled={editRound !== round}
                                onChange={(value) => {
                                  if (newEventResult?.games[round] && value) {
                                    const tempResult = {
                                      ...newEventResult,
                                      games: {
                                        ...newEventResult?.games,
                                        [round]: {
                                          ...newEventResult?.games[round],
                                          win: true,
                                          player: player4,
                                        },
                                      },
                                    }
                                    tempResult && setNewEventResult(getPlayerResultsForEachRound(tempResult))
                                  }
                                }}
                              />
                              <CheckBoxComponent
                                label={<CloseIcon />}
                                value={player4 && newEventResult?.games[round].player === player4 ? !newEventResult?.games[round].won : false}
                                disabled={editRound !== round}
                                onChange={(value) => {
                                  if (newEventResult?.games[round] && value) {
                                    const tempResult = {
                                      ...newEventResult,
                                      games: {
                                        ...newEventResult?.games,
                                        [round]: {
                                          ...newEventResult?.games[round],
                                          win: false,
                                          player: player4,
                                        },
                                      },
                                    }
                                    tempResult && setNewEventResult(getPlayerResultsForEachRound(tempResult))
                                  }
                                }}
                              />
                            </Grid>
                          </Grid>
                        </TableCell>}
                    </>)
                    : (
                      <>
                        {newEventResult?.players &&
                          <EditResultRound
                            id={props.eventResult?.id}
                            round={newEventResult?.games[round]} players={newEventResult?.players}
                            dealer={actualDealer} eventId={props.event.id} gameNumber={round}
                          />}
                      </>
                    )}
                </TableRow>)
            },
            )}
          </TableBody>
          <TableHead>
            <TableRow>
              <TableCell
                className={classes.footerTitle}
                colSpan={2}
              ><Typography variant='caption'>Ergebnis</Typography>
              </TableCell>
              <TableCell
                className={classes.footer}
              >
                <Typography
                  variant='caption'
                > {player1 && newEventResult?.results[player1]?.sessionResult}
                </Typography>
              </TableCell>
              <TableCell
                className={classes.footer}
              >
                <Typography
                  variant='caption'
                > {player2 && newEventResult?.results[player2]?.sessionResult}
                </Typography>
              </TableCell>
              <TableCell
                className={classes.footer}
              >
                <Typography
                  variant='caption'
                > {player3 && newEventResult?.results[player3]?.sessionResult}
                </Typography>
              </TableCell>
              {player4 &&
                <TableCell
                  className={classes.footer}
                >
                  <Typography
                    variant='caption'
                  > {player4 && newEventResult?.results[player4]?.sessionResult}
                  </Typography>
                </TableCell>}
            </TableRow>
            <TableRow>
              <TableCell className={classes.footerTitle} colSpan={2}>
                <Typography variant='caption'>+50 je gew. Spiel <br />
                  -50 je verl. Spiel
                </Typography>
              </TableCell>
              <TableCell
                className={classes.footer}
              >
                <Typography
                  variant='caption'
                >{player1 && (newEventResult?.results[player1]?.offensePoints || 0)}
                </Typography>
              </TableCell>
              <TableCell
                className={classes.footer}
              >
                <Typography
                  variant='caption'
                >{player2 && (newEventResult?.results[player2]?.offensePoints || 0)}
                </Typography>
              </TableCell>
              <TableCell
                className={classes.footer}
              >
                <Typography
                  variant='caption'
                >{player3 && (newEventResult?.results[player3]?.offensePoints || 0)}
                </Typography>
              </TableCell>
              {player4 &&
                <TableCell
                  className={classes.footer}
                >
                  <Typography
                    variant='caption'
                  >{player4 && (newEventResult?.results[player4]?.offensePoints || 0)}
                  </Typography>
                </TableCell>}
            </TableRow>
            <TableRow>
              <TableCell className={classes.footerTitle} colSpan={2}>
                <Typography variant='caption'>+{player4 ? 30 : 40} je verlorenes Spiel des Gegners</Typography>
              </TableCell>
              <TableCell
                className={classes.footer}
              >
                <Typography
                  variant='caption'
                >{player1 && (newEventResult?.results[player1]?.defensePoints || 0)}
                </Typography>
              </TableCell>
              <TableCell
                className={classes.footer}
              >
                <Typography
                  variant='caption'
                >{player2 && (newEventResult?.results[player2]?.defensePoints || 0)}
                </Typography>
              </TableCell>
              <TableCell
                className={classes.footer}
              >
                <Typography
                  variant='caption'
                >{player3 && (newEventResult?.results[player3]?.defensePoints || 0)}
                </Typography>
              </TableCell>
              {player4 &&
                <TableCell
                  className={classes.footer}
                >
                  <Typography
                    variant='caption'
                  >{player4 && (newEventResult?.results[player4]?.defensePoints || 0)}
                  </Typography>
                </TableCell>}
            </TableRow>
            <TableRow>
              <TableCell className={classes.footerTitle} colSpan={2}>
                <Typography
                  variant='caption'
                >Gesamtergebnis
                </Typography>
              </TableCell>
              <TableCell
                className={classes.footer}
              >
                <Typography
                  variant='caption'
                >{player1 && newEventResult?.results[player1]?.completeResult}
                </Typography>
              </TableCell>
              <TableCell
                className={classes.footer}
              >
                <Typography
                  variant='caption'
                >{player2 && newEventResult?.results[player2]?.completeResult}
                </Typography>
              </TableCell>
              <TableCell
                className={classes.footer}
              >
                <Typography
                  variant='caption'
                >{player3 && newEventResult?.results[player3]?.completeResult}
                </Typography>
              </TableCell>
              {player4 &&
                <TableCell
                  className={classes.footer}
                >
                  <Typography
                    variant='caption'
                  >{player4 && newEventResult?.results[player4]?.completeResult}
                  </Typography>
                </TableCell>}
            </TableRow>
          </TableHead>
        </Table>
      )}
      <Box sx={{ marginTop: '20px' }}>
        <ButtonComponent
          disabled={!!editRound || !!newRound}
          text='+ Neues Spiel erfassen'
          onClick={() => {
            if (player1 && player2 && player3 && session && table) {
              const newArray = [...games]
              const playerCount = player4 ? 4 : 3
              const helper = games.length / playerCount
              let tempEventResult: EventResult | undefined = newEventResult ? { ...newEventResult } : undefined

              // neuer Durchgang
              if (helper === 0 || !tempEventResult) {
                const players: string[] = []
                players.push(player1)
                players.push(player2)
                players.push(player3)
                if (player4) {
                  players.push(player4)
                }

                tempEventResult = {
                  eventId: props.event.id,
                  table,
                  session,
                  players,
                  games: {},
                  result: getDefaultCompleteResult(players),
                }
              }
              // neue Runden hinzufügen
              const newgames = tempEventResult && tempEventResult.games ? { ...tempEventResult.games } : {}
              newgames[helper * playerCount + 1] = {
                results: {},
              }
              newArray.push(helper * playerCount + 1)
              setgames(newArray)

              // neuer Dealer als Dealer setzen
              let dealer = player4
              switch (((games.length + 1) % (player4 ? 4 : 3))) {
                case 1:
                  dealer = player1
                  break
                case 2:
                  dealer = player2
                  break
                case 3:
                  dealer = player3
                  break
                case 0:
                  dealer = player4 || player3
                  break
              }
              setActualDealer(dealer)

              if (tempEventResult) {
                tempEventResult.games = newgames
                setNewEventResult(tempEventResult)
                setNewRound(helper * playerCount + 1)
              }
            } else {
              dispatch(settingsActions.setMessage({
                severity: Severity.error,
                message: 'Bitte zunächst Durchgang, Tisch und Spieler eintragen!',
              }))
            }
          }}
        />
      </Box>
    </>
  )
}

export default NewEventResults
